import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import AppDatePicker from "components/AppDatePicker";
import CustomTable from "components/CustomTable";
import Dayjs from "dayjs";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CsvBuilder } from "filefy";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "config/apiRoutes";
import AppTextInput from "components/AppTextInput";
import AppDropDown from "components/AppDropDown";

const PaginationTable = forwardRef(
  (
    {
      title,
      columns,
      apiEndpoint,
      initialFilters = {},
      datePicker = true,
      keywordSearch,
      renderTopToolbar,
      quarterRange,
      allowSearchType,
      searchTypes = [],
      enableEditing = false,
      onEditingRowSave,
      state,
    },
    ref
  ) => {
    const [data, setData] = useState({ records: [], count: 0 });
    const [tableLoading, setTableLoading] = useState(true);
    const [invalidSearch, setInvalidSearch] = useState(false);
    const [searchType, setSearchType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(() =>
      initialFilters.fromDate
        ? Dayjs(initialFilters.fromDate)
        : Dayjs().subtract(7, "day")
    );
    const [to, setTo] = useState(() =>
      initialFilters.toDate
        ? Dayjs(initialFilters.toDate)
        : Dayjs().add(1, "day")
    );
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });

    const token = useSelector((state) => state.auth.token);

    const filteredInitialFilters = Object.keys(initialFilters).reduce(
      (acc, key) => {
        if (initialFilters[key] !== 0) acc[key] = initialFilters[key];
        return acc;
      },
      {}
    );

    useEffect(() => {
      fetchData();
    }, [from, to, pagination.pageIndex, pagination.pageSize, debouncedKeyword, searchType]);

    useEffect(() => {
      // Set a delay (e.g., 500ms) before updating the debounced value
      const handler = setTimeout(() => {
        setDebouncedKeyword(keyword);
      }, 500);

      // Clear the timeout if the keyword changes before the delay ends
      return () => {
        clearTimeout(handler);
      };
    }, [keyword]);

    // Expose the fetchData function to the parent component via ref
    useImperativeHandle(ref, () => ({
      refetch: fetchData, // The parent can call refetch to trigger this function
    }));

    useEffect(() => {
      const selectedType = searchTypes.find(
        (type) => type.value === searchType
      );
      const minLength = selectedType?.minLength || 3;

      setInvalidSearch(keyword.length > 0 && keyword.length < minLength);
    }, [keyword, searchType, searchTypes]);

    useEffect(() => {
      // If search type is not selected and searchTypes exist, default to the first option
      if (
        searchType == null &&
        allowSearchType &&
        keywordSearch &&
        searchTypes.length > 0
      ) {
        setSearchType(searchTypes[0].value);
      }
    }, [searchType, searchTypes, allowSearchType, keywordSearch]);

    const buildKeywordAndTypeQuery = (
      keyword,
      searchType,
      searchTypes,
      keywordSearch
    ) => {
      if (!keywordSearch) return "";

      const selectedType = searchTypes.find(
        (type) => type.value === searchType
      );
      const minLength = selectedType?.minLength || 3;

      const keywordIsValid = keyword && keyword.length >= minLength;

      const keywordQuery = keywordIsValid ? `&keyword=${keyword}` : "";
      const searchTypeQuery =
        keywordIsValid && searchType ? `&type=${searchType}` : "";

      return `${keywordQuery}${searchTypeQuery}`;
    };

    const fetchData = async () => {
      setTableLoading(true);

      const keys = Object.keys(filteredInitialFilters);
      const values = Object.values(filteredInitialFilters);
      const queryParams = keys
        .map((key, index) => `${key}=${values[index]}`)
        .join("&");

      const dateQuery = datePicker
        ? `&from=${from.format("YYYY-MM-DD")}&to=${to.format("YYYY-MM-DD")}${
            queryParams ? `&${queryParams}` : ""
          }`
        : "";

      const keywordTypeQuery = buildKeywordAndTypeQuery(
        keyword,
        searchType,
        searchTypes,
        keywordSearch
      );

      if (invalidSearch) {
        setTableLoading(false);
        return;
      }

      try {
        setInvalidSearch(false);

        const response = await axios.get(
          `${API_BASE_URL}/${apiEndpoint}?limit=${pagination.pageSize}&offset=${
            pagination.pageIndex + 1
          }${dateQuery}${keywordTypeQuery}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const filteredData = response.data.data.records.map((item) => {
          columns.forEach((column) => {
            if (column.mergedKeys) {
              item[column.accessorKey] = column.mergedKeys
                .map((key) => item[key] || "")
                .filter(Boolean)
                .join(" ");
            }
          });
          return item;
        });

        setData({ count: response.data.data.count, records: filteredData });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setTableLoading(false);
      }
    };

    const fetchPage = async (offset,limit) => {
      setLoading(true);

      let keys = Object.keys(filteredInitialFilters);
      let values = Object.values(filteredInitialFilters);
      let queryParams = `${keys
        .map((key, index) => `${key}=${values[index]}`)
        .join("&")}`;
      let dateQuery = datePicker
        ? `from=${from.format("YYYY-MM-DD")}&to=${to.format("YYYY-MM-DD")}${
            queryParams ? `&${queryParams}` : ""
          }`
        : "";

      const keywordTypeQuery = buildKeywordAndTypeQuery(
        keyword,
        searchType,
        searchTypes,
        keywordSearch
      );

      try {
        const response = await axios.get(
          `${API_BASE_URL}/${apiEndpoint}?limit=${limit}&offset=${offset}` +
          `${dateQuery ? `&${dateQuery}` : ""}${keywordTypeQuery}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const filteredData = response.data.data.records.map((item) => {
          columns.forEach((column) => {
            if (column.mergedKeys) {
              item[column.accessorKey] = column.mergedKeys
                .map((key) => item[key] || "")
                .filter(Boolean) // Remove empty strings
                .join(" "); // Join with space
            }
          });
          return item;
        });
        setLoading(false);
        return filteredData;
      } catch (error) {
        setLoading(false);
        console.error("Error fetching data:", error);
        throw error;
      }
    };

     const fetchAllPages = async () => {
       const perPage = 100; 
       let page = 1;
       let all = [];
       let keepFetching = true;

       while (keepFetching) {
         const records = await fetchPage(page, perPage);
        //  stop if nothing comes back
         if (!records || records.length === 0) {
          keepFetching = false;
         } else {
           all = [...all, ...records];
         }
         // stop if last page
         if (records.length < perPage) {
          keepFetching = false;
         } else {
           page ++;
         }
       }
       return all;
     };

    const exportToCSV = async () => {
      try {
        const allData = await fetchAllPages();

        // Check if data is valid
        if (!Array.isArray(allData) || allData.length === 0) {
          console.error(
            "Fetched data is not in the expected format or is empty."
          );
          return;
        }

        // Extract column keys and names
        const columnKeys = columns
          .filter((item) => allData[0].hasOwnProperty(item.accessorKey))
          .map((item) => item.accessorKey);

        const columnNames = columns
          .filter((item) => allData[0].hasOwnProperty(item.accessorKey))
          .map((item) => item.header);

        // Extract rows
        const rows = allData.map((row) => columnKeys.map((col) => row[col]));

        // Build and export the CSV file
        new CsvBuilder(`${title} - ${to.format("YYYY-MM-DD")}`)
          .setDelimeter(",")
          .setColumns(columnNames)
          .addRows(rows)
          .exportFile();
      } catch (error) {
        console.error("Error exporting CSV:", error);
      }
    };

    const searchTypeSelector = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          width: "90px",
          pr: 1,
          height: "50%",
          py: "5px",
        }}
      >
        <AppDropDown
          name="searchType"
          label="Select"
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
          items={searchTypes}
          fullWidth={false}
          shrink={true}
          SelectProps={{
            sx: {
              width: "100%",
              fontSize: "12px",
              paddingY: 0,
              paddingX: "2px",
              height: "30px",
              "& .MuiSelect-select": {
                paddingRight: "8px",
              },
            },
            displayEmpty: true,
          }}
          customClass={{ paddingTop: 0, paddingBottom: 0 }}
        />
      </Box>
    );

    return (
      <CustomTable
        title={title}
        data={data.records}
        columns={columns}
        renderTopToolbar={
          <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
            {keywordSearch && (
              <Grid item>
                <AppTextInput
                  label="Search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  error={invalidSearch}
                  endAdornment={allowSearchType ? searchTypeSelector : ""}
                />
                {invalidSearch && (
                  <Typography sx={{ color: "red", fontSize: "12px" }}>
                    Please enter at least{" "}
                    {searchTypes.find((t) => t.value === searchType)
                      ?.minLength || 3}{" "}
                    characters
                  </Typography>
                )}
              </Grid>
            )}
            <Grid item>
              <AppDatePicker
                label="From"
                value={from}
                onChange={(e) => setFrom(e)}
                maxDate={quarterRange && Dayjs(from).add(3, "month")}
                minDate={
                  quarterRange
                    ? Dayjs(to).subtract(3, "month").add(1, "day")
                    : null
                }
              />
            </Grid>
            <Grid item>
              <AppDatePicker
                label="To"
                value={to}
                onChange={(e) => setTo(e)}
                minDate={quarterRange && Dayjs(to).subtract(3, "month")}
                maxDate={
                  quarterRange
                    ? Dayjs(from).add(3, "month").subtract(1, "day")
                    : null
                }
              />
            </Grid>
            {renderTopToolbar}
            <Grid item sx={{ ml: "auto" }}>
              <LoadingButton
                type="submit"
                onClick={exportToCSV}
                variant="contained"
                color="primary"
                loading={loading}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                CSV
              </LoadingButton>
            </Grid>
          </Grid>
        }
        onPaginationChange={setPagination}
        state={{ pagination, isLoading: tableLoading, ...state }}
        rowCount={data.count}
        enableEditing={enableEditing}
        onEditingRowSave={onEditingRowSave}
      />
    );
  }
);

export default PaginationTable;
